import React, { useState } from 'react';
import { Typography, Box, Tabs, Tab } from '@mui/material';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Heading from 'components/Heading';
import EqualisationAllocationsForm from './EqualisationAllocationsForm';
import { EqualisationFormValues } from './FormWrapper';
import EqualisationsConcertina from 'components/EqualisationElements/EqualisationsConcertina';
import EqualisationRealisationAllocationsForm from './EqualisationRealisationAllocationsForm';
import { AllocationRule } from 'further-types/lp/allocations';
import { useEqualisationSection } from '../EqualisationContext';

interface ActionCardProps {
  action: EqualisationFormValues['actions'][number] & {
    originalRealisationAllocations?: EqualisationFormValues['actions'][number]['linkedActions'][number]['originalRealisationAllocations'];
    newRealisationAllocations?: EqualisationFormValues['actions'][number]['linkedActions'][number]['newRealisationAllocations'];
  };
  actionIndex: number;
  linkedActionIndex?: number;
  baseCurrency: CurrencyCode;
  isLastElement: boolean;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  actionIndex,
  baseCurrency,
  isLastElement,
  linkedActionIndex,
}) => {
  const { closeDate } = useEqualisationSection();
  const isManualAllocationRule =
    action.allocationRule === AllocationRule.Manual;
  const [activeTab, setActiveTab] = useState(1);
  const handleTabChange = (_: unknown, newValue: number) => {
    setActiveTab(newValue);
  };

  const actionTypeLabel =
    action.linkedActions.length > 0
      ? action.actionType +
        ` and ${action.linkedActions.length} ${pluralize(
          'linked action',
          action.linkedActions.length,
        )}`
      : action.actionType;

  const concertinaItems = [
    <Typography key="counterparty" variant="body2">
      {isManualAllocationRule
        ? `${action.counterpartyLabel}*`
        : action.counterpartyLabel}
    </Typography>,
    <Typography key="type" variant="body2">
      {actionTypeLabel}
    </Typography>,
    <Typography key="date" variant="body2">
      {action.ledgerDate.toLocalDateLabel()}
    </Typography>,
    <Typography key="amount" variant="body2">
      {numberToCurrencyString(action.totalAmountLcy, {
        currency: baseCurrency,
      })}
    </Typography>,
  ];

  const expandedContent = (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
      >
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation before {closeDate?.toLocalDateLabel()}
            </Heading>
          }
        />
        <Tab
          label={
            <Heading variant="h5" noMargin>
              Allocation after {closeDate?.toLocalDateLabel()}
            </Heading>
          }
        />
      </Tabs>

      <Box mt={2} mb={2}>
        {activeTab === 0 && (
          <>
            {action.originalAllocations &&
            action.originalAllocations.length > 0 ? (
              <EqualisationAllocationsForm
                fields={action.originalAllocations ?? []}
                inputAmountLcy={action.totalAmountLcy}
                isNotEditable={true}
                actionIndex={actionIndex}
                allocationRule={action.allocationRule}
                allocationType="originalAllocations"
                linkedActionIndex={linkedActionIndex}
                transactionCurrency={baseCurrency}
              />
            ) : (
              <EqualisationRealisationAllocationsForm
                transactionCurrency={baseCurrency}
                allocationRule={action.allocationRule}
                isNotEditable={true}
                actionIndex={actionIndex}
                allocationType="originalRealisationAllocations"
                linkedActionIndex={linkedActionIndex}
              />
            )}
          </>
        )}
        {activeTab === 1 && (
          <>
            {action.newAllocations.length > 0 ? (
              <EqualisationAllocationsForm
                fields={action.newAllocations}
                inputAmountLcy={action.totalAmountLcy}
                isNotEditable={false}
                actionIndex={actionIndex}
                allocationRule={action.allocationRule}
                allocationType="newAllocations"
                linkedActionIndex={linkedActionIndex}
                transactionCurrency={baseCurrency}
              />
            ) : (
              <EqualisationRealisationAllocationsForm
                transactionCurrency={baseCurrency}
                isNotEditable={false}
                actionIndex={actionIndex}
                allocationType="newRealisationAllocations"
                allocationRule={action.allocationRule}
                linkedActionIndex={linkedActionIndex}
              />
            )}
          </>
        )}
      </Box>
      {action.linkedActions.map((linkedAction, linkedIndex) => (
        <ActionCard
          key={linkedIndex}
          action={{
            ...linkedAction,
            linkedActions: [],
            actionType: 'Linked action',
          }}
          actionIndex={actionIndex}
          linkedActionIndex={linkedIndex}
          baseCurrency={baseCurrency}
          isLastElement={linkedIndex === action.linkedActions.length - 1}
        />
      ))}
    </>
  );

  return (
    <EqualisationsConcertina
      isExpandedByDefault={isManualAllocationRule}
      concertinaItems={concertinaItems}
      expandedContent={expandedContent}
      isLastElement={isLastElement}
      gridTemplateColumns="40% 20% 19% 19% 2%"
    />
  );
};

export default ActionCard;
